// @ts-nocheck
/**
 * Helper functions for generating mock widget data
 */

import type { Alert, AlertSeverity, AiInsight, AiInsightCategory } from '../../../../components/advanced/Widgets/types';

/**
 * Generate time-series data with realistic patterns
 */
export function generateTimeSeriesData(
  days: number,
  baseValue: number,
  volatility: number = 0.1,
  trend: 'up' | 'down' | 'flat' = 'flat',
  seasonality: boolean = false
): Array<{ date: string; value: number }> {
  const data: Array<{ date: string; value: number }> = [];
  const now = new Date();

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);

    let value = baseValue;

    // Apply trend
    if (trend === 'up') {
      value += (days - i) * (baseValue * 0.01);
    } else if (trend === 'down') {
      value -= (days - i) * (baseValue * 0.01);
    }

    // Apply seasonality (weekly pattern)
    if (seasonality) {
      const dayOfWeek = date.getDay();
      const weekendFactor = dayOfWeek === 0 || dayOfWeek === 6 ? 0.7 : 1.0;
      value *= weekendFactor;
    }

    // Add random volatility
    value += value * volatility * (Math.random() - 0.5);

    data.push({
      date: date.toISOString().split('T')[0],
      value: Math.round(value),
    });
  }

  return data;
}

/**
 * Generate random alerts
 */
export function generateAlerts(
  count: number,
  severityDistribution: Record<AlertSeverity, number> = {
    info: 0.4,
    warning: 0.3,
    error: 0.2,
    success: 0.1,
  }
): Alert[] {
  const alerts: Alert[] = [];
  const now = new Date();

  const titles = {
    info: [
      'System Update Available',
      'New Feature Released',
      'Maintenance Scheduled',
      'Documentation Updated',
      'Configuration Changed',
    ],
    warning: [
      'High Memory Usage',
      'API Rate Limit Approaching',
      'Disk Space Running Low',
      'SSL Certificate Expiring Soon',
      'Backup Delayed',
    ],
    error: [
      'Database Connection Failed',
      'API Request Failed',
      'Payment Processing Error',
      'Authentication Service Down',
      'Critical Bug Detected',
    ],
    success: [
      'Deployment Successful',
      'Backup Completed',
      'Security Scan Passed',
      'Performance Target Met',
      'Migration Completed',
    ],
  };

  const messages = {
    info: 'This is an informational message. No action required at this time.',
    warning: 'This requires attention soon. Please review and take appropriate action.',
    error: 'Immediate action required. This issue is affecting system functionality.',
    success: 'Operation completed successfully. Everything is working as expected.',
  };

  const categories = ['system', 'security', 'performance', 'business', 'user'] as const;

  for (let i = 0; i < count; i++) {
    // Determine severity based on distribution
    const rand = Math.random();
    let severity: AlertSeverity = 'info';
    let cumulative = 0;
    for (const [sev, prob] of Object.entries(severityDistribution)) {
      cumulative += prob;
      if (rand <= cumulative) {
        severity = sev as AlertSeverity;
        break;
      }
    }

    const titleOptions = titles[severity];
    const title = titleOptions[Math.floor(Math.random() * titleOptions.length)];
    const message = messages[severity];
    const category = categories[Math.floor(Math.random() * categories.length)];

    // Random timestamp within last 30 days
    const timestamp = new Date(now.getTime() - Math.random() * 30 * 24 * 60 * 60 * 1000);

    alerts.push({
      id: `alert-${i + 1}`,
      title,
      message,
      severity,
      category,
      timestamp,
      dismissible: true,
    });
  }

  return alerts.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

/**
 * Generate heatmap data
 */
export function generateHeatmapData(
  rows: number,
  cols: number,
  pattern: 'random' | 'business-hours' | 'seasonal' = 'random'
): Array<{ x: number | string; y: number | string; value: number }> {
  const data: Array<{ x: number | string; y: number | string; value: number }> = [];

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      let value = 0;

      if (pattern === 'random') {
        value = Math.floor(Math.random() * 100);
      } else if (pattern === 'business-hours') {
        // Simulate business hours (9-17) on weekdays
        const isWeekday = y < 5; // Assuming y is day of week (0-6)
        const isBusinessHour = x >= 9 && x <= 17; // Assuming x is hour (0-23)
        value = isWeekday && isBusinessHour
          ? Math.floor(50 + Math.random() * 50)
          : Math.floor(Math.random() * 20);
      } else if (pattern === 'seasonal') {
        // Simulate seasonal pattern
        value = Math.floor(50 + 40 * Math.sin((x / cols) * Math.PI * 2) + Math.random() * 10);
      }

      data.push({ x, y, value });
    }
  }

  return data;
}

/**
 * Generate KPI metric with realistic trends
 */
export interface KpiConfig {
  title: string;
  baseValue: number;
  unit?: string;
  trendDirection?: 'up' | 'down' | 'neutral';
  trendValue?: number;
}

export function generateKpiMetric(config: KpiConfig) {
  const { title, baseValue, unit = '', trendDirection = 'neutral', trendValue } = config;

  const actualTrendValue =
    trendValue !== undefined
      ? trendValue
      : Math.random() * 30 * (trendDirection === 'up' ? 1 : trendDirection === 'down' ? -1 : 0);

  return {
    title,
    value: Math.round(baseValue),
    unit,
    trend:
      trendDirection !== 'neutral'
        ? {
            value: Math.abs(actualTrendValue),
            direction: trendDirection,
            isPositive: trendDirection === 'up',
          }
        : undefined,
  };
}

/**
 * Generate AI insights
 */
export function generateAiInsights(count: number): AiInsight[] {
  const insights: AiInsight[] = [];
  const now = new Date();

  const templates = {
    trend: [
      {
        title: 'User Activity Trending Upward',
        content: 'User engagement has increased by **23%** over the past week. This trend is primarily driven by new feature adoption and improved onboarding flows.',
      },
      {
        title: 'Revenue Growth Acceleration',
        content: 'Monthly recurring revenue is growing **15% faster** than the previous quarter. Key drivers include:\n- Enterprise plan upgrades\n- Reduced churn rate\n- International expansion',
      },
    ],
    anomaly: [
      {
        title: 'Unusual Spike in API Errors',
        content: 'API error rate increased by **340%** between 2-4 PM today. Investigation suggests a third-party service degradation affecting authentication endpoints.',
      },
      {
        title: 'Traffic Pattern Deviation',
        content: 'Website traffic dropped **45%** on Tuesday compared to weekly average. This appears correlated with a temporary CDN issue in the EU region.',
      },
    ],
    recommendation: [
      {
        title: 'Optimize Database Query Performance',
        content: 'Analysis shows **12 slow queries** consuming 60% of database resources. Recommended actions:\n- Add composite indexes\n- Implement query result caching\n- Review N+1 query patterns',
      },
      {
        title: 'Scale Infrastructure Proactively',
        content: 'Current growth trajectory suggests you will reach **80% capacity** within 3 weeks. Consider scaling up compute resources or implementing auto-scaling policies now.',
      },
    ],
    forecast: [
      {
        title: 'Q4 Revenue Projection',
        content: 'Based on current trends, projected Q4 revenue is **$2.4M** (±8%). This represents **32% growth** compared to Q3 and aligns with annual targets.',
      },
      {
        title: 'User Growth Forecast',
        content: 'Expected to reach **50,000 active users** by end of month. Growth rate is consistent with marketing campaign performance and conversion funnel metrics.',
      },
    ],
  };

  const categories: AiInsightCategory[] = ['trend', 'anomaly', 'recommendation', 'forecast'];

  for (let i = 0; i < count; i++) {
    const category = categories[Math.floor(Math.random() * categories.length)];
    const categoryTemplates = templates[category];
    const template = categoryTemplates[Math.floor(Math.random() * categoryTemplates.length)];

    const timestamp = new Date(now.getTime() - Math.random() * 7 * 24 * 60 * 60 * 1000);
    const confidence = 0.5 + Math.random() * 0.49; // 0.5 to 0.99

    insights.push({
      id: `insight-${i + 1}`,
      title: template.title,
      content: template.content,
      confidence,
      category,
      timestamp,
    });
  }

  return insights.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}
